import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import {
  Activity,
  AlertCircle,
  ArrowUpDown,
  ArrowUpRight,
  BadgeCheck,
  CircleAlert,
  FileWarning,
  Loader,
  Medal,
  Search,
  Send,
  ShieldAlert,
  Store,
  TrendingUp,
  Trophy,
  Truck,
  XCircle,
} from "lucide-react";

// ============================================================
// CHART CONFIGURATION
// ============================================================

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
);

const CHART_COLORS = ["#f97316", "#fb923c", "#fdba74", "#f59e0b", "#ea580c"];

const PIE_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "bottom" },
  },
};

const BAR_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
  },
  scales: {
    x: {
      ticks: { color: "#475569" },
      grid: { display: false },
    },
    y: {
      ticks: { color: "#475569" },
      grid: { color: "#e5e7eb" },
    },
  },
};

const STATUS_ORDER = [
  "successful",
  "completed",
  "delivered",
  "assigned",
  "acknowledged",
  "arrived",
  "food is ready",
  "unassigned",
  "cancelled",
  "canceled",
  "declined",
  "failed",
  "rejected",
];

// ============================================================
// REUSABLE CARDS
// ============================================================

function KpiCard({ label, value, description, Icon, textColor, iconBg }) {
  return (
    <div className="rounded-2xl border bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <h3 className={`mt-1 text-3xl font-bold ${textColor}`}>{value}</h3>
        </div>

        <div
          className={`flex h-11 w-11 items-center justify-center rounded-xl ${iconBg}`}
        >
          <Icon className="h-5 w-5" />
        </div>
      </div>

      <p className="mt-3 text-xs text-gray-500">{description}</p>
    </div>
  );
}

function AttentionCard({ title, children, tone = "orange" }) {
  const tones = {
    orange: "border-orange-100 bg-orange-50 text-orange-800",
    amber: "border-amber-100 bg-amber-50 text-amber-800",
    red: "border-red-100 bg-red-50 text-red-800",
    slate: "border-slate-200 bg-slate-50 text-slate-800",
  };

  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`}>
      <p className="font-medium">{title}</p>
      <p className="mt-1 opacity-90">{children}</p>
    </div>
  );
}

function PerformanceBadge({ label, value, detail, Icon, color }) {
  return (
    <div className="rounded-2xl border bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${color}`}
        >
          <Icon className="h-5 w-5" />
        </div>

        <div className="min-w-0">
          <p className="text-sm text-gray-500">{label}</p>
          <p className="break-words text-base font-semibold text-gray-900">
            {value}
          </p>
          <p className="text-xs text-gray-500">{detail}</p>
        </div>
      </div>
    </div>
  );
}

function TopRankingItem({ rank, name, detail, count, share }) {
  return (
    <div className="rounded-xl border p-3 transition hover:bg-orange-50">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">
            #{rank} {name}
          </p>
          <p className="mt-1 text-xs text-gray-500">{detail}</p>
        </div>

        <div className="shrink-0 text-right">
          <p className="text-lg font-bold text-gray-900">{count}</p>
          <p className="text-xs text-gray-500">{share}%</p>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// COMPONENT 1 - REPORTS OVERVIEW
// KPI CARDS / HERO / ATTENTION / PERFORMANCE
// ============================================================

export function ReportsOverview({ stats }) {
  const {
    totalStatusTasks,
    successfulTasks,
    cancelledTasks,
    activePipelineTasks,
    successRate,
    cancelRate,
    busiestShop,
    secondBusiestShop,
    top2ShopShare,
    avgTasksPerShop,
    avgTasksPerDriver,
    unknownShopCount,
    unknownDriverCount,
    topPerformerDriver,
    lowestPerformerDriver,
    shopsCount,
    driversCount,
  } = stats;

  const kpis = [
    {
      label: "Total Tasks",
      value: totalStatusTasks,
      description: "Status-based overall task volume",
      Icon: Activity,
      textColor: "text-gray-900",
      iconBg: "bg-orange-50 text-orange-600",
    },
    {
      label: "Success Rate",
      value: `${successRate}%`,
      description: `${successfulTasks} successful tasks`,
      Icon: BadgeCheck,
      textColor: "text-green-600",
      iconBg: "bg-green-50 text-green-600",
    },
    {
      label: "Cancelled",
      value: cancelledTasks,
      description: `${cancelRate}% of total status volume`,
      Icon: XCircle,
      textColor: "text-red-600",
      iconBg: "bg-red-50 text-red-600",
    },
    {
      label: "Active Pipeline",
      value: activePipelineTasks,
      description: "Acknowledged / arrived / ready / unassigned",
      Icon: Loader,
      textColor: "text-orange-600",
      iconBg: "bg-orange-50 text-orange-600",
    },
    {
      label: "Shop Concentration",
      value: `${top2ShopShare}%`,
      description: "Share of top 2 shops in total shop volume",
      Icon: TrendingUp,
      textColor: "text-orange-600",
      iconBg: "bg-orange-50 text-orange-600",
    },
  ];

  const quickStats = [
    { label: "Shops Covered", value: shopsCount },
    { label: "Drivers Active", value: driversCount },
    { label: "Avg / Shop", value: avgTasksPerShop },
    { label: "Avg / Driver", value: avgTasksPerDriver },
  ];

  return (
    <>
      {/* KPI CARDS */}
      <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {kpis.map((item) => (
          <KpiCard key={item.label} {...item} />
        ))}
      </div>

      {/* HERO + ATTENTION */}
      <div className="mb-5 grid grid-cols-1 gap-4 xl:grid-cols-3">
        {/* HERO OVERVIEW */}
        <div className="relative overflow-hidden rounded-[28px] border border-orange-200 bg-gradient-to-br from-[#FC981B] via-[#F59E0B] to-[#F97316] p-6 text-white shadow-[0_10px_30px_rgba(252,152,27,0.18)] md:p-7 xl:col-span-2">
          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-12 -left-12 h-44 w-44 rounded-full bg-white/10 blur-3xl" />

          <div className="relative z-10 flex items-start justify-between gap-4">
            <div>
              <p className="mb-2 text-sm font-medium tracking-wide text-white/80">
                Quick Overview
              </p>

              <h3 className="mb-3 text-2xl font-bold leading-tight md:text-3xl">
                Packky task performance overview
              </h3>

              <p className="max-w-3xl text-sm leading-7 text-white/90 md:text-[15px]">
                Delivery performance is strong with{" "}
                <span className="font-semibold text-white">{successRate}%</span>{" "}
                successful tasks and only{" "}
                <span className="font-semibold text-white">{cancelRate}%</span>{" "}
                cancellations in this range. Most volume is coming from{" "}
                <span className="font-semibold text-white">
                  {busiestShop?.shopName || "—"}
                </span>{" "}
                and{" "}
                <span className="font-semibold text-white">
                  {secondBusiestShop?.shopName || "—"}
                </span>
                , so these shops should remain the top focus for operations,
                staffing, and dispatch readiness.
              </p>
            </div>

            <div className="hidden h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-white/25 bg-white/15 shadow-lg backdrop-blur-md md:flex">
              <ArrowUpRight className="h-7 w-7 text-white" />
            </div>
          </div>

          <div className="relative z-10 mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
            {quickStats.map((item) => (
              <div
                key={item.label}
                className="rounded-2xl border border-white/20 bg-white/14 p-4 shadow-sm backdrop-blur-md"
              >
                <p className="text-xs uppercase tracking-wide text-white/75">
                  {item.label}
                </p>

                <p className="mt-2 text-2xl font-bold">{item.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ATTENTION POINTS */}
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <CircleAlert className="h-5 w-5 text-orange-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Attention Points
            </h3>
          </div>

          <div className="space-y-3 text-sm">
            <AttentionCard title="Top shop load">
              {busiestShop?.shopName || "—"} handles{" "}
              {busiestShop?.taskCount || 0} tasks.
            </AttentionCard>

            <AttentionCard title="Volume concentration" tone="amber">
              Top 2 shops contribute {top2ShopShare}% of mapped shop volume.
            </AttentionCard>

            <AttentionCard title="Cancellation watch" tone="red">
              {cancelledTasks} tasks are cancelled in this date range.
            </AttentionCard>

            <AttentionCard title="Data hygiene" tone="slate">
              {unknownShopCount} shop rows and {unknownDriverCount} driver rows
              have incomplete mapping.
            </AttentionCard>
          </div>
        </div>
      </div>

      {/* PERFORMANCE BADGES */}
      <div className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-3">
        <PerformanceBadge
          label="Top Performer"
          value={topPerformerDriver?.driverName || "—"}
          detail={`${topPerformerDriver?.taskCount || 0} tasks`}
          Icon={Medal}
          color="bg-green-50 text-green-600"
        />

        <PerformanceBadge
          label="Lowest Performer"
          value={lowestPerformerDriver?.driverName || "—"}
          detail={`${lowestPerformerDriver?.taskCount || 0} tasks`}
          Icon={ShieldAlert}
          color="bg-orange-50 text-orange-600"
        />

        <PerformanceBadge
          label="Unknown Mapping"
          value={`${unknownShopCount + unknownDriverCount} rows`}
          detail="Incomplete shop or driver details"
          Icon={FileWarning}
          color="bg-red-50 text-red-600"
        />
      </div>
    </>
  );
}

// ============================================================
// STATUS HELPERS
// ============================================================

function formatStatusLabel(value = "") {
  return value
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getStatusTone(status = "") {
  const value = status.toLowerCase();

  if (["successful", "completed", "delivered"].includes(value)) {
    return {
      color: "#16a34a",
      bg: "#dcfce7",
      border: "#bbf7d0",
      Icon: BadgeCheck,
      label: "Healthy",
    };
  }

  if (
    ["cancelled", "canceled", "declined", "failed", "rejected"].includes(value)
  ) {
    return {
      color: "#dc2626",
      bg: "#fee2e2",
      border: "#fecaca",
      Icon: XCircle,
      label: "Needs attention",
    };
  }

  if (value === "food is ready") {
    return {
      color: "#f59e0b",
      bg: "#fef3c7",
      border: "#fde68a",
      Icon: Send,
      label: "Ready",
    };
  }

  if (["assigned", "acknowledged", "arrived"].includes(value)) {
    return {
      color: "#f97316",
      bg: "#ffedd5",
      border: "#fed7aa",
      Icon: Activity,
      label: "In progress",
    };
  }

  if (value === "unassigned") {
    return {
      color: "#9333ea",
      bg: "#f3e8ff",
      border: "#e9d5ff",
      Icon: CircleAlert,
      label: "Open",
    };
  }

  return {
    color: "#64748b",
    bg: "#f1f5f9",
    border: "#e2e8f0",
    Icon: AlertCircle,
    label: "Other",
  };
}

// ============================================================
// REUSABLE STATUS CARD
// ============================================================

function StatusSummaryCard({ card }) {
  const Icon = card.Icon;

  return (
    <div
      className="rounded-2xl border p-4"
      style={{
        backgroundColor: card.bg,
        borderColor: card.bg,
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-gray-600">{card.label}</p>

          <p className="mt-1 text-2xl font-bold text-gray-900">{card.value}</p>

          <p className="mt-1 text-xs text-gray-600">
            {card.percentage}% of total
          </p>
        </div>

        <div
          className="flex h-11 w-11 items-center justify-center rounded-xl bg-white"
          style={{ color: card.color }}
        >
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

// ============================================================
// REUSABLE STATUS DETAIL ROW
// ============================================================

function StatusDetailRow({ item, isHighest }) {
  const Icon = item.tone.Icon;

  return (
    <div
      className="rounded-2xl border bg-white p-4 transition hover:shadow-sm"
      style={{ borderColor: item.tone.border }}
    >
      <div className="mb-3 flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
            style={{
              backgroundColor: item.tone.bg,
              color: item.tone.color,
            }}
          >
            <Icon className="h-5 w-5" />
          </div>

          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-gray-900">
              {formatStatusLabel(item.taskStatus)}
            </p>

            <p className="text-xs text-gray-500">
              {item.tone.label}
              {isHighest ? " • Highest volume" : ""}
            </p>
          </div>
        </div>

        <div className="shrink-0 text-right">
          <p className="text-lg font-bold text-gray-900">{item.taskCount}</p>

          <p className="text-xs font-medium" style={{ color: item.tone.color }}>
            {item.percentage}%
          </p>
        </div>
      </div>

      <div className="h-2.5 overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${item.percentage}%`,
            backgroundColor: item.tone.color,
          }}
        />
      </div>
    </div>
  );
}

// ============================================================
// COMPONENT 2 - REPORTS ANALYTICS
// STATUS / TOP SHOPS / TOP DRIVERS / CHARTS
// ============================================================

export function ReportsAnalytics({ stats, statuses = [] }) {
  const {
    totalStatusTasks,
    totalRestaurantTasks,
    totalDriverTasks,
    successfulTasks,
    cancelledTasks,
    activePipelineTasks,
    successRate,
    cancelRate,
    topShops,
    topDrivers,
  } = stats;

  const statusRows = [...statuses]
    .map((item) => ({
      ...item,
      percentage: totalStatusTasks
        ? ((item.taskCount / totalStatusTasks) * 100).toFixed(1)
        : "0.0",
      tone: getStatusTone(item.taskStatus),
    }))
    .sort((a, b) => {
      const aIndex = STATUS_ORDER.indexOf(a.taskStatus.toLowerCase());
      const bIndex = STATUS_ORDER.indexOf(b.taskStatus.toLowerCase());

      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    });

  const highestStatus = [...statusRows].sort(
    (a, b) => b.taskCount - a.taskCount,
  )[0]?.taskStatus;

  const summaryCards = [
    {
      label: "Successful",
      value: successfulTasks,
      percentage: successRate,
      Icon: BadgeCheck,
      color: "#16a34a",
      bg: "#dcfce7",
    },
    {
      label: "Active Pipeline",
      value: activePipelineTasks,
      percentage: totalStatusTasks
        ? ((activePipelineTasks / totalStatusTasks) * 100).toFixed(1)
        : "0.0",
      Icon: Activity,
      color: "#f97316",
      bg: "#ffedd5",
    },
    {
      label: "Cancelled",
      value: cancelledTasks,
      percentage: cancelRate,
      Icon: XCircle,
      color: "#dc2626",
      bg: "#fee2e2",
    },
  ];

  const shopPieData = {
    labels: topShops.map((shop) => shop.shopName),
    datasets: [
      {
        data: topShops.map((shop) => shop.taskCount),
        backgroundColor: CHART_COLORS,
        borderWidth: 0,
      },
    ],
  };

  const driverBarData = {
    labels: topDrivers.map((driver) => driver.driverName),
    datasets: [
      {
        label: "Tasks",
        data: topDrivers.map((driver) => driver.taskCount),
        backgroundColor: CHART_COLORS,
        borderRadius: 8,
        maxBarThickness: 40,
      },
    ],
  };

  return (
    <>
      {/* STATUS BREAKDOWN + TOP SHOPS */}
      <div className="mb-5 grid grid-cols-1 gap-4 xl:grid-cols-3">
        {/* STATUS BREAKDOWN */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm xl:col-span-2">
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <Activity className="h-5 w-5 text-orange-600" />
                Status Breakdown
              </h3>

              <p className="mt-1 text-xs text-gray-500">
                Clear count, percentage and operational health by status
              </p>
            </div>

            <div className="rounded-xl border border-orange-100 bg-orange-50 px-3 py-2 text-right">
              <p className="text-[11px] font-medium text-orange-600">
                Total Tasks
              </p>
              <p className="text-lg font-bold text-orange-700">
                {totalStatusTasks}
              </p>
            </div>
          </div>

          {/* THREE STATUS SUMMARY CARDS */}
          <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-3">
            {summaryCards.map((card) => (
              <StatusSummaryCard key={card.label} card={card} />
            ))}
          </div>

          {/* ALL STATUS ROWS */}
          {statusRows.length === 0 ? (
            <div className="flex h-56 items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50">
              <div className="text-center">
                <AlertCircle className="mx-auto mb-2 h-8 w-8 text-gray-400" />
                <p className="text-sm font-medium text-gray-700">
                  No status data found
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Try changing the selected date range
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {statusRows.map((item) => (
                <StatusDetailRow
                  key={item.taskStatus}
                  item={item}
                  isHighest={item.taskStatus === highestStatus}
                />
              ))}
            </div>
          )}
        </div>

        {/* TOP SHOPS + PIE CHART */}
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Store className="h-5 w-5 text-orange-600" />
              Top Shops
            </h3>

            <span className="text-xs text-gray-500">By task count</span>
          </div>

          <div className="space-y-3">
            {topShops.map((shop, index) => (
              <TopRankingItem
                key={`${shop.shopCode}-${index}`}
                rank={index + 1}
                name={shop.shopName}
                detail={`Code: ${shop.shopCode}`}
                count={shop.taskCount}
                share={
                  totalRestaurantTasks
                    ? ((shop.taskCount / totalRestaurantTasks) * 100).toFixed(1)
                    : "0.0"
                }
              />
            ))}
          </div>

          <div className="mt-5 h-64">
            <Pie data={shopPieData} options={PIE_OPTIONS} />
          </div>
        </div>
      </div>

      {/* TOP DRIVERS + BAR CHART */}
      <div className="mb-5 grid grid-cols-1 gap-4 xl:grid-cols-3">
        {/* TOP DRIVERS */}
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Trophy className="h-5 w-5 text-orange-600" />
              Top Drivers
            </h3>

            <span className="text-xs text-gray-500">Highest task volume</span>
          </div>

          <div className="space-y-3">
            {topDrivers.map((driver, index) => (
              <TopRankingItem
                key={`${driver.driverCode}-${index}`}
                rank={index + 1}
                name={driver.driverName}
                detail={`${driver.driverCode} • ${driver.driverMobile}`}
                count={driver.taskCount}
                share={
                  totalDriverTasks
                    ? ((driver.taskCount / totalDriverTasks) * 100).toFixed(1)
                    : "0.0"
                }
              />
            ))}
          </div>
        </div>

        {/* DRIVER CONTRIBUTION BAR CHART */}
        <div className="rounded-2xl border bg-white p-5 shadow-sm xl:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Truck className="h-5 w-5 text-orange-600" />
              Driver Contribution
            </h3>

            <span className="text-xs text-gray-500">Top 5 comparison</span>
          </div>

          <div className="h-80">
            <Bar data={driverBarData} options={BAR_OPTIONS} />
          </div>
        </div>
      </div>
    </>
  );
}

// ============================================================
// REUSABLE SEARCH + SORT CONTROLS
// ============================================================

function RankingControls({ search, setSearch, sortBy, setSortBy, isShop }) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={
            isShop ? "Search shop or code..." : "Search driver, code, mobile..."
          }
          className={`h-10 w-full rounded-xl border pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-orange-200 ${
            isShop ? "sm:w-52" : "sm:w-56"
          }`}
        />
      </div>

      <div className="relative">
        <ArrowUpDown className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

        <select
          value={sortBy}
          onChange={(event) => setSortBy(event.target.value)}
          className="h-10 w-full rounded-xl border bg-white pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-orange-200 sm:w-44"
        >
          <option value="tasks_desc">Tasks: High to Low</option>
          <option value="tasks_asc">Tasks: Low to High</option>
          <option value="name_asc">Name: A to Z</option>
          <option value="name_desc">Name: Z to A</option>
        </select>
      </div>
    </div>
  );
}

// ============================================================
// REUSABLE SHOP / DRIVER RANKING TABLE
// ============================================================

function RankingTable({
  isShop,
  rows,
  totalCount,
  totalRestaurantTasks,
  search,
  setSearch,
  sortBy,
  setSortBy,
}) {
  return (
    <div className="rounded-2xl border bg-white p-5 shadow-sm">
      {/* TABLE HEADER */}
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {isShop ? "All Shops Ranking" : "All Drivers Ranking"}
          </h3>

          <p className="mt-1 text-xs text-gray-500">
            {rows.length} visible of {totalCount} {isShop ? "shops" : "drivers"}
          </p>
        </div>

        <RankingControls
          search={search}
          setSearch={setSearch}
          sortBy={sortBy}
          setSortBy={setSortBy}
          isShop={isShop}
        />
      </div>

      {/* TABLE CONTENT */}
      <div className="overflow-x-auto rounded-xl border">
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 z-10 bg-orange-50">
            <tr className="border-b text-left">
              <th className="px-3 py-3 font-semibold text-gray-700">#</th>

              <th className="px-3 py-3 font-semibold text-gray-700">
                {isShop ? "Shop" : "Driver"}
              </th>

              <th className="px-3 py-3 font-semibold text-gray-700">Code</th>

              {!isShop && (
                <th className="px-3 py-3 font-semibold text-gray-700">
                  Mobile
                </th>
              )}

              <th className="px-3 py-3 text-right font-semibold text-gray-700">
                Tasks
              </th>

              {isShop && (
                <th className="px-3 py-3 text-right font-semibold text-gray-700">
                  Share
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  {isShop ? "No shop data found" : "No driver data found"}
                </td>
              </tr>
            ) : (
              rows.map((item, index) => (
                <tr
                  key={`${isShop ? item.shopCode : item.driverCode}-${index}`}
                  className="border-b last:border-b-0 hover:bg-orange-50/40"
                >
                  <td className="px-3 py-3">{index + 1}</td>

                  <td className="whitespace-nowrap px-3 py-3 font-medium text-gray-900">
                    {isShop ? item.shopName : item.driverName}
                  </td>

                  <td className="whitespace-nowrap px-3 py-3 text-gray-500">
                    {isShop ? item.shopCode : item.driverCode}
                  </td>

                  {!isShop && (
                    <td className="whitespace-nowrap px-3 py-3 text-gray-500">
                      {item.driverMobile}
                    </td>
                  )}

                  <td className="whitespace-nowrap px-3 py-3 text-right font-semibold">
                    {item.taskCount}
                  </td>

                  {isShop && (
                    <td className="whitespace-nowrap px-3 py-3 text-right text-gray-500">
                      {totalRestaurantTasks
                        ? (
                            (item.taskCount / totalRestaurantTasks) *
                            100
                          ).toFixed(1)
                        : "0.0"}
                      %
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// COMPONENT 3 - REPORTS RANKINGS
// ALL SHOPS / ALL DRIVERS
// ============================================================

export function ReportsRankings({
  shops = [],
  drivers = [],
  shopsCount = 0,
  driversCount = 0,
  totalRestaurantTasks = 0,

  shopSearch,
  setShopSearch,
  driverSearch,
  setDriverSearch,

  shopSortBy,
  setShopSortBy,
  driverSortBy,
  setDriverSortBy,
}) {
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <RankingTable
        isShop
        rows={shops}
        totalCount={shopsCount}
        totalRestaurantTasks={totalRestaurantTasks}
        search={shopSearch}
        setSearch={setShopSearch}
        sortBy={shopSortBy}
        setSortBy={setShopSortBy}
      />

      <RankingTable
        isShop={false}
        rows={drivers}
        totalCount={driversCount}
        search={driverSearch}
        setSearch={setDriverSearch}
        sortBy={driverSortBy}
        setSortBy={setDriverSortBy}
      />
    </div>
  );
}
